/**
 * React wrapper for uPlot: measures its container, builds the chart from the
 * caller's options and rebuilds on data or option changes (the series are
 * tiny, so a rebuild is cheaper than diffing).
 */
import { useLayoutEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { cn } from '@/lib/cn';

export interface UPlotChartProps {
  data: uPlot.AlignedData;
  /** Options for a given plot size; width and height are set by the wrapper. */
  buildOptions: (width: number, height: number) => Omit<uPlot.Options, 'width' | 'height'>;
  height: number;
  className?: string;
  /** Called with the chart after each build (for overlays that need valToPos). */
  onReady?: (chart: uPlot) => void;
}

export function UPlotChart({ data, buildOptions, height, className, onReady }: UPlotChartProps) {
  const host = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    let chart: uPlot | null = null;
    const build = () => {
      const width = Math.max(120, Math.floor(el.clientWidth));
      chart?.destroy();
      chart = new uPlot({ ...buildOptions(width, height), width, height }, data, el);
      onReady?.(chart);
    };
    build();
    const observer = new ResizeObserver(() => {
      if (!chart) return;
      const width = Math.max(120, Math.floor(el.clientWidth));
      if (width !== chart.width) chart.setSize({ width, height });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      chart?.destroy();
      chart = null;
    };
  }, [data, buildOptions, height, onReady]);

  return <div ref={host} className={cn('twin-chart w-full', className)} style={{ height }} />;
}
